/**
 * Aide d'affichage TOTP : formate le code courant d'une entree
 * (`EntrySecrets.totpSecret`) et le temps restant avant renouvellement. Pure
 * fonction au-dessus de `@klavox/crypto-core#totp` -- aucune minuterie ici,
 * l'UI (`ui/app.ts`) rappelle `computeTotpDisplay` a intervalle regulier.
 */
import { parseTotpSeed, totp, totpSecondsRemaining } from '@klavox/crypto-core';

export interface TotpDisplay {
  /** Code groupe par 3 chiffres pour la lisibilite (ex. "123 456") ; un code Steam Guard (« FR8RV ») reste d'un bloc. */
  readonly formattedCode: string;
  readonly secondsRemaining: number;
  readonly periodSeconds: number;
}

/** Groupe une chaine de chiffres par blocs de 3 (RFC 6238 recommande 6-8 chiffres). */
export function groupTotpCode(code: string): string {
  const groups: string[] = [];
  for (let i = 0; i < code.length; i += 3) groups.push(code.slice(i, i + 3));
  return groups.join(' ');
}

export interface ComputeTotpDisplayInput {
  /** Base32 ou URI `otpauth://totp` (ses parametres sont respectes). */
  readonly secret: string;
  readonly periodSeconds?: number;
  readonly digits?: number;
  readonly timestampMs?: number;
}

export function computeTotpDisplay(input: ComputeTotpDisplayInput): TotpDisplay {
  const seed = parseTotpSeed(input.secret);
  const periodSeconds = input.periodSeconds ?? seed.periodSeconds;
  const code = totp({
    secret: input.secret,
    periodSeconds,
    digits: input.digits,
    timestampMs: input.timestampMs,
  });
  return {
    formattedCode: seed.encoder === 'steam' ? code : groupTotpCode(code),
    secondsRemaining: totpSecondsRemaining(periodSeconds, input.timestampMs),
    periodSeconds,
  };
}
